import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { DocumentDTO } from '@almanac/shared';
import { DocumentEditDrawer } from '../components/DocumentEditDrawer.js';
import { DocumentListItem } from '../components/DocumentListItem.js';
import { DocumentPreview } from '../components/DocumentPreview.js';
import { LoadStatus } from '../components/LoadStatus.js';
import { PersonAvatar } from '../components/PersonAvatar.js';
import {
  type DocumentFilter,
  type DocumentFolder,
  type DocumentLens,
  type FacetCount,
  NO_FACET,
  applyFilters,
  distinctKinds,
  distinctSubAreas,
  distinctTags,
  documentDate,
  kindCounts,
  subAreaCounts,
  yearCounts,
  documentMatchesQuery,
  expiringCount,
  groupByRecency,
  isExpiryHandled,
  groupByYear,
  groupDocuments,
  groupDocumentsByArea,
  groupDocumentsByKind,
  groupDocumentsByPerson,
  recentlyAdded,
  useDocuments,
} from '../lib/documents.js';
import { useLifeAreas, useNowMinute } from '../lib/obligations.js';
import { isAcceptedDocFile } from '../lib/files.js';
import { personInitials, usePeople } from '../lib/people.js';

const LENSES: { key: DocumentLens; label: string; title: string }[] = [
  { key: 'person', label: 'Person', title: 'By person' },
  { key: 'kind', label: 'Kind', title: 'By kind' },
  { key: 'area', label: 'Area', title: 'By area' },
];

function lensTitle(type: DocumentLens): string {
  return LENSES.find((l) => l.key === type)?.title ?? 'Documents';
}

export function DocumentsPage() {
  const documents = useDocuments();
  const lifeAreas = useLifeAreas();
  const people = usePeople();
  const nowMs = useNowMinute();
  const now = useMemo(() => new Date(nowMs), [nowMs]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // The index axis (folders) and the active drill/search state. No
  // filters + no query = the index; a folder drill sets a primary filter;
  // typing a query switches to search results.
  const [lens, setLens] = useState<DocumentLens>('person');
  const [filters, setFilters] = useState<DocumentFilter[]>([]);
  // The tags index — a destination of its own (reached from a tag
  // drill's breadcrumb), listing every tag with its count. Not a lens:
  // tags stay out of the primary folder chrome.
  const [showingTags, setShowingTags] = useState(false);
  const [query, setQuery] = useState('');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  // Search is a magnifying-glass toggle in the header, not permanent
  // chrome — mirrors the Obligations tab exactly. `searchOpen` controls
  // whether the input renders; clearing it collapses back to the icon.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  function closeSearch() {
    setSearchOpen(false);
    setQuery('');
  }

  // `target` drives the create/edit drawer; `preview` the read-first card.
  const [target, setTarget] = useState<DocumentDTO | 'new' | null>(null);
  const [preview, setPreview] = useState<DocumentDTO | null>(null);
  // Desktop drag-and-drop: a file dropped onto the page opens the drawer
  // pre-attached. `dragDepth` smooths nested dragenter/leave.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);

  const peopleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of people.data ?? []) map.set(p.id, p.displayName);
    return map;
  }, [people.data]);
  const areaById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of lifeAreas.data ?? []) map.set(a.id, a.name);
    return map;
  }, [lifeAreas.data]);

  // Deep-link from a person card: /documents?person=<id> lands directly
  // in that person's drill-down. Waits for the people query so the
  // filter chip gets its real label, then consumes the param.
  useEffect(() => {
    const personId = searchParams.get('person');
    if (!personId || !people.data) return;
    setLens('person');
    setFilters([
      { type: 'person', id: personId, label: peopleById.get(personId) ?? 'Person' },
    ]);
    const next = new URLSearchParams(searchParams);
    next.delete('person');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, people.data, peopleById]);

  // Deep-link from the Today card's "See all": /documents?expiring=1
  // lands on the expiry attention view.
  useEffect(() => {
    if (!searchParams.get('expiring')) return;
    setFilters([{ type: 'expiry', label: 'Expiring soon' }]);
    const next = new URLSearchParams(searchParams);
    next.delete('expiring');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Deep-link from a document card's tag chip: /documents?tag=<value>
  // shows everything carrying that tag.
  useEffect(() => {
    const tag = searchParams.get('tag');
    if (!tag) return;
    setFilters([{ type: 'tag', value: tag, label: `#${tag}` }]);
    const next = new URLSearchParams(searchParams);
    next.delete('tag');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Deep-link from a person card's key-document row: /documents?open=<id>
  // opens that document's read card. Waits for the list so the id
  // resolves, then consumes the param.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || !documents.data) return;
    const doc = documents.data.find((d) => d.id === openId);
    if (doc) setPreview(doc);
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, documents.data]);

  const allDocs = useMemo(() => documents.data ?? [], [documents.data]);
  const trimmedQuery = query.trim();
  const inSearch = trimmedQuery.length > 0;
  const inIndex = filters.length === 0 && !inSearch;
  const primary = filters.find(
    (f) =>
      f.type === 'person' ||
      f.type === 'kind' ||
      f.type === 'area' ||
      f.type === 'expiry' ||
      f.type === 'tag',
  );

  // ── Folder lists per axis ────────────────────────────────────────────
  // Shared by the index (folders for the active lens) and the desktop
  // drill-down rail (siblings of the drilled folder, whose axis can
  // differ from the lens when arriving via search or a deep link).
  const axisFolders = useMemo(() => {
    return (axis: DocumentLens): DocumentFolder[] => {
      if (axis === 'person') {
        return groupDocumentsByPerson(allDocs, peopleById).map((g) => {
          const id = g.key === 'person:none' ? NO_FACET : g.key.slice('person:'.length);
          const label = g.key === 'person:none' ? 'Unassigned' : g.label;
          return {
            key: g.key,
            label,
            initials: personInitials(label),
            count: g.documents.length,
            filter: { type: 'person', id, label },
          };
        });
      }
      if (axis === 'kind') {
        return groupDocumentsByKind(allDocs).map((g) => {
          const isNone = g.key === 'kind:none';
          return {
            key: g.key,
            label: g.label,
            count: g.documents.length,
            filter: { type: 'kind', value: isNone ? NO_FACET : g.label, label: g.label },
          };
        });
      }
      return groupDocumentsByArea(allDocs, areaById).map((g) => {
        const id = g.key.slice('area:'.length);
        return {
          key: g.key,
          label: g.label,
          count: g.documents.length,
          filter: { type: 'area', id, label: g.label },
        };
      });
    };
  }, [allDocs, peopleById, areaById]);

  const folders = useMemo<DocumentFolder[]>(() => axisFolders(lens), [axisFolders, lens]);

  // ── Results for the drill / search view ─────────────────────────────
  const results = useMemo(() => {
    let docs = applyFilters(allDocs, filters, now);
    if (inSearch) {
      const q = trimmedQuery.toLowerCase();
      docs = docs.filter(
        (d) =>
          documentMatchesQuery(d, trimmedQuery) ||
          (d.pertainsTo ?? []).some((id) => (peopleById.get(id) ?? '').toLowerCase().includes(q)),
      );
    }
    return docs;
  }, [allDocs, filters, now, inSearch, trimmedQuery, peopleById]);

  // The meta line under each row varies by context (a person folder
  // already says whose it is, etc.).
  function metaFor(doc: DocumentDTO): string {
    const about = (doc.pertainsTo ?? [])
      .map((id) => peopleById.get(id))
      .filter((n): n is string => Boolean(n))
      .join(', ');
    const kind = doc.category ?? '';
    const area = areaById.get(doc.lifeAreaId) ?? '';
    const subArea = doc.subArea ?? '';
    let parts: string[];
    if (inSearch || primary?.type === 'expiry') parts = [about, kind, area, subArea];
    else if (primary?.type === 'person') parts = [kind, area, subArea];
    else if (primary?.type === 'kind') parts = [about, doc.issuer ?? '', subArea];
    else if (primary?.type === 'area') parts = [about, kind, subArea];
    else parts = [about, kind, subArea]; // index "recently added", tag drills
    // In the expiry lens, mark rows whose reminder the user has already
    // dismissed or turned into a renewal task — they still list (the
    // fact stands) but no longer count toward the attention surfaces.
    if (primary?.type === 'expiry' && isExpiryHandled(doc)) parts.push('handled');
    return parts.filter(Boolean).join(' · ');
  }

  // ── Drill-down facets (contextual second axis) ──────────────────────
  // Each facet is one quiet chip ("Kind · All ▾") opening a picker sheet
  // that lists every value with its count — the values themselves no
  // longer pile up as pills on the page. A facet's options ignore its
  // own active choice (so switching stays one tap) but respect the rest.
  // "Kinds" offered inside a person/area folder.
  const kindFacet = useMemo(() => {
    if (inSearch || (primary?.type !== 'person' && primary?.type !== 'area')) return null;
    const base = applyFilters(
      allDocs,
      filters.filter((f) => f.type !== 'kind'),
      now,
    );
    const values = kindCounts(base);
    return values.length > 0 ? { values, total: base.length } : null;
  }, [inSearch, primary, allDocs, filters, now]);
  // Sub-areas in every drill-down whose documents carry any — person and
  // kind folders included, so "David's dental paperwork" is two taps.
  // Views with no sub-areas in them look unchanged.
  const subAreaFacet = useMemo(() => {
    if (inSearch || !primary) return null;
    const base = applyFilters(
      allDocs,
      filters.filter((f) => f.type !== 'subarea'),
      now,
    );
    const values = subAreaCounts(base);
    return values.length > 0 ? { values, total: base.length } : null;
  }, [inSearch, primary, allDocs, filters, now]);
  // "Years" in every drill-down — but only once the folder actually
  // spans more than one year (every document has a date, so a
  // single-year facet would be permanent chrome with nothing to filter).
  const yearFacet = useMemo(() => {
    if (inSearch || !primary) return null;
    const base = applyFilters(
      allDocs,
      filters.filter((f) => f.type !== 'year'),
      now,
    );
    const values = yearCounts(base);
    return values.length > 1 ? { values, total: base.length } : null;
  }, [inSearch, primary, allDocs, filters, now]);
  const activeKind = filters.find((f) => f.type === 'kind');
  const activeSubArea = filters.find((f) => f.type === 'subarea');
  const activeYear = filters.find((f) => f.type === 'year');

  // Which facet's picker sheet is open, if any. Leaving the drill (back
  // to the index, another folder, search) closes it.
  const [facetSheet, setFacetSheet] = useState<'subarea' | 'kind' | 'year' | null>(null);
  useEffect(() => {
    if (!primary || inSearch) setFacetSheet(null);
  }, [primary, inSearch]);

  function setSecondary(next: DocumentFilter | null, dropType: DocumentFilter['type']) {
    setFilters((prev) => {
      const kept = prev.filter((f) => f.type !== dropType);
      return next ? [...kept, next] : kept;
    });
  }

  // ── Search "+ Filter" suggestions ───────────────────────────────────
  const filterSuggestions = useMemo<DocumentFilter[]>(() => {
    if (!filterMenuOpen) return [];
    const hasPerson = filters.some((f) => f.type === 'person');
    const hasKind = filters.some((f) => f.type === 'kind');
    const hasArea = filters.some((f) => f.type === 'area');
    const out: DocumentFilter[] = [];
    if (!hasPerson) {
      for (const g of groupDocumentsByPerson(results, peopleById)) {
        if (g.key === 'person:none') continue;
        out.push({ type: 'person', id: g.key.slice('person:'.length), label: g.label });
      }
    }
    if (!hasKind) {
      for (const k of distinctKinds(results)) out.push({ type: 'kind', value: k, label: k });
    }
    if (!hasArea) {
      for (const g of groupDocumentsByArea(results, areaById)) {
        out.push({ type: 'area', id: g.key.slice('area:'.length), label: g.label });
      }
    }
    if (!filters.some((f) => f.type === 'subarea')) {
      for (const s of distinctSubAreas(results)) {
        out.push({ type: 'subarea', value: s, label: s });
      }
    }
    if (!filters.some((f) => f.type === 'tag')) {
      // '#' marks tags apart from kinds/sub-areas in the menu.
      for (const t of distinctTags(results).slice(0, 6)) {
        out.push({ type: 'tag', value: t, label: `#${t}` });
      }
    }
    return out.slice(0, 16);
  }, [filterMenuOpen, filters, results, peopleById, areaById]);

  function drillInto(folder: DocumentFolder) {
    setFilters([folder.filter]);
    setQuery('');
    setFilterMenuOpen(false);
  }
  function backToIndex() {
    setFilters([]);
    setQuery('');
    setFilterMenuOpen(false);
    setShowingTags(false);
  }

  // The tags index: like backToIndex, but landing on the tag list.
  function goToTags() {
    setFilters([]);
    setQuery('');
    setFilterMenuOpen(false);
    setShowingTags(true);
  }
  function removeFilter(target: DocumentFilter) {
    setFilters((prev) => prev.filter((f) => f !== target));
  }

  // ── Drag-and-drop ───────────────────────────────────────────────────
  function isFileDrag(e: React.DragEvent) {
    return Array.from(e.dataTransfer?.types ?? []).includes('Files');
  }
  function onDragEnter(e: React.DragEvent) {
    if (!isFileDrag(e)) return;
    // While the edit drawer is open, the page never hijacks a drag —
    // the drawer's own attach zone is the drop target. (Flipping the
    // open drawer to 'new' here used to blank a mid-edit form.)
    if (target !== null) return;
    dragDepth.current += 1;
    setDragActive(true);
  }
  function onDragLeave(e: React.DragEvent) {
    if (!isFileDrag(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  }
  function onDrop(e: React.DragEvent) {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    if (target !== null) return;
    const file = e.dataTransfer.files?.[0];
    if (!file || !isAcceptedDocFile(file)) return;
    // A drop while a record's read card is open attaches to *that*
    // record: its edit drawer opens with the file staged and every
    // field carrying the record's existing metadata. A drop anywhere
    // else starts a new document.
    if (preview) {
      const doc = preview;
      setPreview(null);
      setPendingFile(file);
      setTarget(doc);
      return;
    }
    setPendingFile(file);
    setTarget('new');
  }

  // ── Result grouping ─────────────────────────────────────────────────
  const dated = useMemo(() => {
    if (inSearch) return [];
    if (primary?.type === 'kind') return groupByYear(results);
    if (primary?.type === 'expiry') return groupDocuments(results, now);
    if (primary) return groupByRecency(results, now);
    return [];
  }, [inSearch, primary, results, now]);
  const searchResults = useMemo(
    () =>
      inSearch
        ? [...results].sort((a, b) => documentDate(b).getTime() - documentDate(a).getTime())
        : [],
    [inSearch, results],
  );

  const expiring = expiringCount(allDocs, now);

  // Header copy.
  const eyebrow = inIndex && showingTags
    ? 'All tags'
    : inIndex
    ? 'All documents'
    : inSearch
      ? 'Find a document'
      : primary?.type === 'expiry'
        ? 'Needs attention'
        : `${results.length} ${results.length === 1 ? 'document' : 'documents'}`;
  const title = inIndex && showingTags
    ? 'Tags'
    : inIndex
    ? lensTitle(lens)
    : inSearch
      ? 'Search'
      : primary?.type === 'expiry'
        ? 'Expiring soon'
        : (primary?.label ?? 'Documents');

  // Both the drawer and the preview read from the live cache so an
  // optimistic update reflects immediately while they're open.
  const editingTarget =
    target && target !== 'new'
      ? (documents.data?.find((d) => d.id === target.id) ?? target)
      : target;
  const previewLive = preview
    ? (documents.data?.find((d) => d.id === preview.id) ?? preview)
    : null;

  const recents = useMemo(() => recentlyAdded(allDocs), [allDocs]);

  const inTags = inIndex && showingTags;
  const tagList = useMemo(() => {
    const names = distinctTags(allDocs);
    return names.map((tag) => ({
      tag,
      count: allDocs.filter((d) =>
        (d.tags ?? []).some((t) => t.trim().toLowerCase() === tag.toLowerCase()),
      ).length,
    }));
  }, [allDocs]);

  // ── Desktop drill-down rail ──────────────────────────────────────────
  // Siblings of the drilled folder (hop between people/kinds/areas
  // without stepping back up), tag siblings for a tag drill, and a
  // small stats card. The expiry view has no sibling axis — it renders
  // without a rail.
  const inDrill = !inIndex && !inSearch;
  const railFolders = useMemo<DocumentFolder[]>(() => {
    if (!inDrill || !primary) return [];
    if (primary.type === 'person' || primary.type === 'kind' || primary.type === 'area') {
      return axisFolders(primary.type);
    }
    return [];
  }, [inDrill, primary, axisFolders]);
  const railTags = inDrill && primary?.type === 'tag' ? tagList : [];

  function isCurrentFolder(fl: DocumentFilter): boolean {
    const p = primary;
    if (!p || fl.type !== p.type) return false;
    if (fl.type === 'person' && p.type === 'person') return fl.id === p.id;
    if (fl.type === 'kind' && p.type === 'kind') return fl.value === p.value;
    if (fl.type === 'area' && p.type === 'area') return fl.id === p.id;
    return false;
  }

  // Stats describe the drilled folder as a whole (ignoring secondary
  // chips) so the numbers don't jump around while the user filters.
  const drillGlance = useMemo(() => {
    if (!inDrill || !primary || primary.type === 'expiry') return null;
    const docs = applyFilters(allDocs, [primary], now);
    const soon = expiringCount(docs, now);
    const starred = docs.filter((d) => d.starred).length;
    return soon > 0 || starred > 0 ? { soon, starred } : null;
  }, [inDrill, primary, allDocs, now]);
  const showRail = railFolders.length > 1 || railTags.length > 1 || drillGlance !== null;

  return (
    <section
      className={dragActive ? 'doc-page doc-page--drag' : 'doc-page'}
      onDragEnter={onDragEnter}
      onDragOver={(e) => {
        if (isFileDrag(e)) e.preventDefault();
      }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragActive && (
        <div className="doc-dropzone" aria-hidden>
          <div className="doc-dropzone__inner">
            {preview ? `Drop to attach to “${preview.title}”` : 'Drop to add a document'}
          </div>
        </div>
      )}

      <header className="page-header page-header--with-action">
        <div className="page-header__lead">
          <button
            type="button"
            className="page-header__icon-button page-header__back"
            aria-label={inTags ? 'Back to index' : inIndex ? 'Back to home' : 'Back to index'}
            onClick={() => (inTags ? backToIndex() : inIndex ? navigate('/') : backToIndex())}
          >
            <BackIcon />
          </button>
          <div>
            <div className="section__label">{eyebrow}</div>
            <h2 className="page-header__title">{title}</h2>
          </div>
        </div>
        <button
          type="button"
          className={`page-header__icon-button ${searchOpen ? 'page-header__icon-button--active' : ''}`}
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
          aria-label={searchOpen ? 'Close search' : 'Search documents'}
          aria-expanded={searchOpen}
        >
          <SearchIcon />
        </button>
      </header>

      {!inIndex && !inSearch && primary && primary.type !== 'expiry' && (
        <nav className="doc-crumb" aria-label="Breadcrumb">
          <button type="button" className="doc-crumb__link" onClick={backToIndex}>
            Documents
          </button>
          <ChevronRight />
          {/* The lens level is a real step back too: return to the index
              with that lens active (it may differ from the current one
              when the drill came from search or a deep link). A tag
              drill steps back to the tags index instead. */}
          <button
            type="button"
            className="doc-crumb__link"
            onClick={() => {
              if (primary.type === 'tag') {
                goToTags();
                return;
              }
              setLens(primary.type as DocumentLens);
              backToIndex();
            }}
          >
            {primary.type === 'tag' ? 'Tags' : lensTitle(primary.type as DocumentLens)}
          </button>
          <ChevronRight />
          <span className="doc-crumb__here">{primary.label}</span>
        </nav>
      )}

      {inTags && !inSearch && (
        <nav className="doc-crumb" aria-label="Breadcrumb">
          <button type="button" className="doc-crumb__link" onClick={backToIndex}>
            Documents
          </button>
          <ChevronRight />
          <span className="doc-crumb__here">Tags</span>
        </nav>
      )}

      {searchOpen && (
        <div className="obligations-search">
          <input
            ref={searchInputRef}
            type="search"
            className="obligations-search__input"
            placeholder="Search by title, issuer, person, or tag…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeSearch();
            }}
            aria-label="Search documents"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            type="button"
            className="obligations-search__clear"
            onClick={closeSearch}
            aria-label="Close search"
          >
            ×
          </button>
        </div>
      )}

      <LoadStatus
        isLoading={documents.isLoading}
        isError={documents.isError}
        error={documents.error}
      />

      {/* ── TAGS INDEX ────────────────────────────────────────── */}
      {inTags && (
        <div className="page-measure">
          {documents.isSuccess && tagList.length === 0 && (
            <p className="empty-state">
              No tags yet. Tags come from the details you (or Read with AI) add to a
              document — they show here once any exist.
            </p>
          )}
          {tagList.length > 0 && (
            <>
              <div className="doc-seclabel">Most used</div>
              <div className="doc-folders">
                {tagList.map(({ tag, count }) => (
                  <button
                    key={tag}
                    type="button"
                    className="doc-folder"
                    onClick={() => {
                      setShowingTags(false);
                      setFilters([{ type: 'tag', value: tag, label: `#${tag}` }]);
                    }}
                  >
                    <span className="doc-folder__av doc-folder__av--facet" aria-hidden>
                      #
                    </span>
                    <span className="doc-folder__name">{tag}</span>
                    <span className="doc-folder__count">{count}</span>
                    <ChevronRight className="doc-folder__chev" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── INDEX ─────────────────────────────────────────────── */}
      {/* On phones .docs-columns is a plain block (order unchanged);
          ≥840px it splits: the working flow + folders left, "Recently
          added" as an always-visible rail right — same arrangement as
          the Today screen. */}
      {inIndex && !showingTags && (
        <div className="docs-columns">
          <div className="docs-main">
          {expiring > 0 && (
            <button
              type="button"
              className="doc-attention"
              onClick={() => setFilters([{ type: 'expiry', label: 'Expiring soon' }])}
            >
              <WarnIcon />
              {expiring} {expiring === 1 ? 'document' : 'documents'} expiring soon
              <span className="doc-attention__go">View ›</span>
            </button>
          )}

          <button type="button" className="documents-add" onClick={() => setTarget('new')}>
            <span className="documents-add__plus" aria-hidden>
              +
            </span>
            Add a document
          </button>

          <div className="lens-switch" role="tablist" aria-label="Group documents by">
            {LENSES.map((l) => (
              <button
                key={l.key}
                type="button"
                role="tab"
                aria-selected={lens === l.key}
                className={`lens-switch__option ${lens === l.key ? 'lens-switch__option--active' : ''}`}
                onClick={() => setLens(l.key)}
              >
                {l.label}
              </button>
            ))}
          </div>

          {documents.isSuccess && allDocs.length === 0 && (
            <p className="empty-state">
              No documents yet. Add one — its title, who it&rsquo;s about, kind, and the file —
              and it&rsquo;ll file itself here under every facet at once.
            </p>
          )}

          {folders.length > 0 && (
            <>
              <div className="doc-seclabel">Folders</div>
              <div className="doc-folders">
                {folders.map((f) => {
                  // Person folders show the person's photo avatar when
                  // one is set (same circle as the People tool);
                  // initials cover Unassigned and photo-less people.
                  const filter = f.filter;
                  const person =
                    lens === 'person' && filter.type === 'person'
                      ? people.data?.find((p) => p.id === filter.id)
                      : undefined;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      className="doc-folder"
                      onClick={() => drillInto(f)}
                    >
                      {lens === 'person' ? (
                        person ? (
                          <PersonAvatar
                            person={person}
                            className="doc-folder__av doc-folder__av--person"
                          />
                        ) : (
                          <span className="doc-folder__av doc-folder__av--person" aria-hidden>
                            {f.initials}
                          </span>
                        )
                      ) : (
                        <span className="doc-folder__av doc-folder__av--facet" aria-hidden>
                          <FolderGlyph />
                        </span>
                      )}
                      <span className="doc-folder__name">{f.label}</span>
                      <span className="doc-folder__count">{f.count}</span>
                      <ChevronRight className="doc-folder__chev" />
                    </button>
                  );
                })}
              </div>
            </>
          )}

          </div>

          {recents.length > 0 && (
            <aside className="docs-rail">
              <div className="doc-seclabel">Recently added</div>
              <div className="doc-folders">
                {recents.map((d) => (
                  <DocumentListItem key={d.id} document={d} meta={metaFor(d)} now={now} onOpen={setPreview} />
                ))}
              </div>
            </aside>
          )}
        </div>
      )}

      {/* ── DRILL-DOWN (filters, no query) ────────────────────── */}
      {!inIndex && !inSearch && (
        <div className="page-columns">
        <div className="page-measure">
          {(subAreaFacet || kindFacet || yearFacet) && (
            <div className="doc-chips">
              {subAreaFacet && (
                <FacetChip
                  name="Sub-area"
                  value={activeSubArea?.label}
                  onOpen={() => setFacetSheet('subarea')}
                />
              )}
              {kindFacet && (
                <FacetChip
                  name="Kind"
                  value={activeKind?.label}
                  onOpen={() => setFacetSheet('kind')}
                />
              )}
              {yearFacet && (
                <FacetChip
                  name="Year"
                  value={activeYear?.label}
                  onOpen={() => setFacetSheet('year')}
                />
              )}
            </div>
          )}

          {results.length === 0 && (
            <p className="empty-state">Nothing here yet.</p>
          )}

          {dated.map((group) => (
            <section key={group.key} className="doc-dategroup">
              <h3 className="doc-datehead">
                {group.label}
                <span className="doc-datehead__count">· {group.documents.length}</span>
              </h3>
              <div className="doc-folders">
                {group.documents.map((d) => (
                  <DocumentListItem key={d.id} document={d} meta={metaFor(d)} now={now} onOpen={setPreview} />
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Desktop-only sibling rail (display:none below 840px). */}
        {showRail && (
          <aside className="page-rail">
            {railFolders.length > 1 && (
              <nav
                className="rail-card"
                aria-label={
                  primary?.type === 'person'
                    ? 'Other people'
                    : primary?.type === 'kind'
                      ? 'Other kinds'
                      : 'Other areas'
                }
              >
                <div className="doc-seclabel">
                  {primary?.type === 'person'
                    ? 'People'
                    : primary?.type === 'kind'
                      ? 'Kinds'
                      : 'Areas'}
                </div>
                {railFolders.map((f) => {
                  const filter = f.filter;
                  const current = isCurrentFolder(filter);
                  const person =
                    filter.type === 'person'
                      ? people.data?.find((p) => p.id === filter.id)
                      : undefined;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      className={`doc-folder ${current ? 'doc-folder--current' : ''}`}
                      onClick={() => drillInto(f)}
                      aria-current={current || undefined}
                    >
                      {filter.type === 'person' ? (
                        person ? (
                          <PersonAvatar
                            person={person}
                            className="doc-folder__av doc-folder__av--person"
                          />
                        ) : (
                          <span className="doc-folder__av doc-folder__av--person" aria-hidden>
                            {f.initials}
                          </span>
                        )
                      ) : (
                        <span className="doc-folder__av doc-folder__av--facet" aria-hidden>
                          <FolderGlyph />
                        </span>
                      )}
                      <span className="doc-folder__name">{f.label}</span>
                      <span className="doc-folder__count">{f.count}</span>
                    </button>
                  );
                })}
              </nav>
            )}

            {railTags.length > 1 && primary?.type === 'tag' && (
              <nav className="rail-card" aria-label="Other tags">
                <div className="doc-seclabel">Tags</div>
                {railTags.map(({ tag, count }) => {
                  const current =
                    primary.type === 'tag' &&
                    tag.toLowerCase() === primary.value.toLowerCase();
                  return (
                    <button
                      key={tag}
                      type="button"
                      className={`doc-folder ${current ? 'doc-folder--current' : ''}`}
                      onClick={() =>
                        setFilters([{ type: 'tag', value: tag, label: `#${tag}` }])
                      }
                      aria-current={current || undefined}
                    >
                      <span className="doc-folder__av doc-folder__av--facet" aria-hidden>
                        #
                      </span>
                      <span className="doc-folder__name">{tag}</span>
                      <span className="doc-folder__count">{count}</span>
                    </button>
                  );
                })}
              </nav>
            )}

            {drillGlance && (
              <div className="rail-card">
                <div className="doc-seclabel">At a glance</div>
                <div className="rail-glance">
                  {drillGlance.soon > 0 && (
                    <div className="rail-glance__line">
                      <strong>{drillGlance.soon}</strong> expiring soon
                    </div>
                  )}
                  {drillGlance.starred > 0 && (
                    <div className="rail-glance__line">
                      <strong>{drillGlance.starred}</strong> key{' '}
                      {drillGlance.starred === 1 ? 'document' : 'documents'} starred
                    </div>
                  )}
                </div>
              </div>
            )}
          </aside>
        )}
        </div>
      )}

      {/* ── SEARCH (query active) ─────────────────────────────── */}
      {inSearch && (
        <div className="page-measure">
          <div className="doc-chips">
            {filters.map((f, i) => (
              <button
                key={`${f.type}-${i}`}
                type="button"
                className="doc-chip doc-chip--on"
                onClick={() => removeFilter(f)}
              >
                {f.label} <span className="doc-chip__x" aria-hidden>×</span>
              </button>
            ))}
            <button
              type="button"
              className="doc-chip doc-chip--add"
              onClick={() => setFilterMenuOpen((v) => !v)}
              aria-expanded={filterMenuOpen}
            >
              + Filter
            </button>
          </div>

          {filterMenuOpen && filterSuggestions.length > 0 && (
            <div className="doc-chips doc-chips--menu">
              {filterSuggestions.map((s, i) => (
                <button
                  key={`${s.type}-${i}`}
                  type="button"
                  className="doc-chip"
                  onClick={() => {
                    setFilters((prev) => [...prev, s]);
                    setFilterMenuOpen(false);
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          <div className="doc-seclabel">
            {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'}
            <span className="doc-seclabel__n"> · newest first</span>
          </div>
          {searchResults.length === 0 ? (
            <p className="empty-state">No matches for &ldquo;{trimmedQuery}&rdquo;.</p>
          ) : (
            <div className="doc-folders">
              {searchResults.map((d) => (
                <DocumentListItem key={d.id} document={d} meta={metaFor(d)} now={now} onOpen={setPreview} />
              ))}
            </div>
          )}
        </div>
      )}

      {facetSheet === 'subarea' && subAreaFacet && (
        <FacetSheet
          title="Sub-area"
          allLabel="All sub-areas"
          facet={subAreaFacet}
          active={activeSubArea?.type === 'subarea' ? activeSubArea.value : undefined}
          onPick={(v) => {
            setSecondary(v == null ? null : { type: 'subarea', value: String(v), label: String(v) }, 'subarea');
            setFacetSheet(null);
          }}
          onClose={() => setFacetSheet(null)}
        />
      )}
      {facetSheet === 'kind' && kindFacet && (
        <FacetSheet
          title="Kind"
          allLabel="All kinds"
          facet={kindFacet}
          active={activeKind?.type === 'kind' ? activeKind.value : undefined}
          onPick={(v) => {
            setSecondary(v == null ? null : { type: 'kind', value: String(v), label: String(v) }, 'kind');
            setFacetSheet(null);
          }}
          onClose={() => setFacetSheet(null)}
        />
      )}
      {facetSheet === 'year' && yearFacet && (
        <FacetSheet
          title="Year"
          allLabel="All years"
          facet={yearFacet}
          active={activeYear?.type === 'year' ? activeYear.value : undefined}
          onPick={(v) => {
            setSecondary(v == null ? null : { type: 'year', value: Number(v), label: String(v) }, 'year');
            setFacetSheet(null);
          }}
          onClose={() => setFacetSheet(null)}
        />
      )}

      {previewLive && (
        <DocumentPreview
          document={previewLive}
          lifeAreaName={areaById.get(previewLive.lifeAreaId)}
          onClose={() => setPreview(null)}
          onEdit={() => {
            const doc = previewLive;
            setPreview(null);
            setTarget(doc);
          }}
        />
      )}

      <DocumentEditDrawer
        target={editingTarget}
        initialFile={pendingFile}
        onClose={() => {
          setTarget(null);
          setPendingFile(null);
        }}
      />
    </section>
  );
}

/** One quiet drill-down facet chip — "Kind · All ▾" — turning terracotta
 *  when a value is chosen. Opens the facet's picker sheet. */
function FacetChip({
  name,
  value,
  onOpen,
}: {
  name: string;
  value: string | undefined;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className={`doc-facet ${value ? 'doc-facet--on' : ''}`}
      onClick={onOpen}
      aria-haspopup="dialog"
    >
      <span className="doc-facet__k">{name}</span> {value ?? 'All'}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>
  );
}

/** The facet picker — the app's standard bottom sheet (side panel on
 *  desktop) listing every value with its count, one choice per facet.
 *  "All" resets; picking anything closes the sheet. */
function FacetSheet({
  title,
  allLabel,
  facet,
  active,
  onPick,
  onClose,
}: {
  title: string;
  allLabel: string;
  facet: { values: FacetCount<string | number>[]; total: number };
  active: string | number | undefined;
  onPick: (value: string | number | null) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  function row(label: string, count: number, on: boolean, pick: string | number | null) {
    return (
      <button
        key={pick == null ? '·all' : String(pick)}
        type="button"
        className={`doc-facetrow ${on ? 'doc-facetrow--on' : ''}`}
        onClick={() => onPick(pick)}
        aria-pressed={on}
      >
        <span className="doc-facetrow__name">{label}</span>
        <span className="doc-facetrow__count">{count}</span>
        {on ? (
          <svg
            className="doc-facetrow__check"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        ) : (
          <span className="doc-facetrow__spacer" aria-hidden />
        )}
      </button>
    );
  }
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer__grip">
          <div className="drawer__handle" aria-hidden />
          <header className="drawer__header">
            <h2 className="drawer__title">{title}</h2>
          </header>
        </div>
        <div className="drawer__body">
          <div className="doc-facetlist">
            {row(allLabel, facet.total, active == null, null)}
            {facet.values.map((o) => row(String(o.value), o.count, active === o.value, o.value))}
          </div>
        </div>
      </aside>
    </>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  // Inline SVG so the icon inherits its colour from the button via
  // `currentColor` — identical to the Obligations search toggle.
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M13 13l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FolderGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
